<template>
  <div class="max-w-12xl mx-auto text-left md:h-full">
    <div class="md:h-full">
      <div
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:h-full"
      >
        <div class="college-search flex flex-col">
          <header class="bg-white py-6 px-6 fade-in">
            <h1 class="text-3xl font-extrabold mb-1">Course Finder</h1>
            <div class="text-sm">
              Find any course at a {{ VUE_APP_STATE_NAME }} two-year college.
            </div>
            <div class="text-xs mt-1">
              <em
                >Sample only. Full courses and catalogs are available with
                subscription</em
              >
            </div>
          </header>

          <div class="bg-white sm:rounded-lg">
            <div class="px-4 py-5 sm:px-6 sm:py-3 fade-in">
              <div class="flex flex-row justify-between">
                <h2 class="text-2xl leading-6 font-medium text-green-800 mb-1">
                  {{
                    selectedCollegeName
                      ? "Selected College"
                      : "Search for a College"
                  }}
                </h2>
                <div v-if="selectedCollegeName">
                  <button
                    class="btn border border-green-400 bg-green-300 text-gray-500 shadow-md px-4 rounded-lg"
                    @click="resetSearch"
                  >
                    reset
                  </button>
                </div>
              </div>
              <div
                v-if="!selectedCollegeName"
                class="mt-2 max-w-xl text-sm text-gray-500"
              >
                <p>Enter the first few characters of the college name</p>
              </div>
              <div
                v-if="!selectedCollegeName"
                class="relative mt-5 sm:flex sm:items-center"
              >
                <input
                  type="text"
                  placeholder="Search for a college"
                  class="input-text"
                  v-model="query"
                  @input="filterColleges"
                  @focus="toggleDropdown(true)"
                  @blur="toggleDropdown(false)"
                />
                <div
                  v-if="showDropdown"
                  class="absolute z-10 bg-white top-5 mt-5 w-full border border-gray-200 rounded shadow-lg"
                >
                  <div
                    v-for="college in filteredColleges"
                    :key="college.id"
                    class="cursor-pointer hover:bg-gray-200 p-2"
                    @click="
                      setInstitutionId(college.id, college.institutionName)
                    "
                  >
                    {{ college.institutionName }}
                  </div>
                </div>
              </div>
              <div class="mt-2">
                <div
                  v-if="selectedCollegeName"
                  class="mt-3 text-gray-800 text-sm font-bold"
                >
                  {{ selectedCollegeName }}
                </div>
              </div>
            </div>

            <transition name="fade">
              <div
                class="px-4 py-5 sm:px-6 sm:py-3 border-t-1 fade-in"
                v-if="!isLoadingCatalogs && catalogs.length >= 1"
              >
                <h2 class="text-2xl leading-6 font-medium text-green-800 mb-3">
                  College Year
                </h2>
                <select v-model="catalogId" class="input-text mt-3">
                  <option value="" disabled>Select a catalog</option>
                  <option
                    v-for="catalog in sortedCatalogs"
                    :key="catalog.id"
                    :value="catalog.id"
                  >
                    {{ catalog.edition }}
                  </option>
                </select>
              </div>
              <div
                v-else-if="selectedCollegeName && !isLoadingCatalogs"
                class="px-4 py-5 sm:px-6 sm:py-3 border-t-1 fade-in"
              >
                <p class="text-gray-500">
                  There are no catalogs with courses available for this college.
                </p>
              </div>
              <div
                v-else-if="selectedCollegeName && isLoadingCatalogs"
                class="px-4 py-5 sm:px-6 sm:py-3 border-t-1 fade-in"
              >
                <p class="text-gray-500">Loading catalogs...</p>
              </div>
            </transition>

            <transition name="fade">
              <div class="px-4 py-5 sm:px-6 sm:py-3 fade-in" v-if="catalogId">
                <h2 class="text-2xl leading-6 font-medium text-green-800 mb-3">
                  Find a course
                </h2>
                <div class="mt-2 max-w-xl text-sm text-gray-500">
                  <p>Search by subject code, number or search term.</p>
                </div>
                <div class="relative mt-3 sm:flex sm:items-center">
                  <input
                    type="text"
                    :placeholder="
                      isLoadingCourses
                        ? 'Loading courses...'
                        : 'Search for a course'
                    "
                    class="input-text"
                    v-model="courseName"
                    @input="filterCourses"
                    @focus="showCourseDropdown = true"
                    @blur="handleBlur"
                    @click="showCourseDropdown = true"
                    @keydown.down.prevent="highlightNext"
                    @keydown.up.prevent="highlightPrevious"
                    @keydown.enter.prevent="selectHighlightedCourse"
                    :disabled="isLoadingCourses"
                    ref="courseInput"
                  />
                  <div
                    v-if="isLoadingCourses"
                    class="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <div
                      class="animate-spin rounded-full h-5 w-5 border-b-2 border-green-800"
                    ></div>
                  </div>
                  <div
                    v-if="showCourseDropdown"
                    class="absolute z-10 bg-white top-5 mt-5 w-full border border-gray-200 rounded shadow-lg max-h-[300px] overflow-y-scroll"
                    style="max-height: 300px"
                  >
                    <div
                      v-if="!courseName && filteredCourses.length === 0"
                      class="p-2 text-gray-500"
                    >
                      Begin your search.
                    </div>
                    <div
                      v-else-if="filteredCourses.length === 0"
                      class="p-2 text-gray-500"
                    >
                      No courses found.
                    </div>
                    <div
                      v-for="(course, index) in filteredCourses"
                      :key="course.course_code"
                      class="cursor-pointer hover:bg-gray-200 p-2"
                      :class="{ 'bg-gray-200': index === highlightedIndex }"
                      @click="setCourseId(course.course_code)"
                      @mousedown.prevent
                    >
                      {{ course.course_code }} - {{ course.title }}
                    </div>
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <footer
            class="mt-auto bg-white py-8 px-6 border-t-1 border-solid border-indigo-500 flex flex-col md:flex-row"
          >
            <a href="https://degreedata.com" target="_blank">
              <img
                src="https://degreedata.com/wp-content/uploads/2020/12/degreedata_logo.png"
                alt="DegreeData Logo"
                class="w-40 flex-none mb-3"
              />
            </a>
            <div class="flex-auto md:text-right text-sm">
              &copy; 2023 DegreeData, LLC
            </div>
          </footer>
        </div>

        <div class="course-details lg:col-span-2 relative px-6 py-6">
          <transition name="fade">
            <div class="sticky top-5 mt-0" v-if="course">
              <div class="bg-white shadow-inner sm:rounded-lg">
                <div class="px-4 py-5 sm:p-6">
                  <h3
                    class="text-2xl leading-6 font-medium text-green-800 mb-3"
                  >
                    {{ course.courseNumber }}
                  </h3>
                  <div class="mt-3">
                    <h2
                      class="text-gray-900 text-2xl font-medium mb-2 text-green-800"
                    >
                      {{ course.title }}
                    </h2>
                    <h3
                      class="text-gray-900 text-lg font-medium mb-2 text-green-800"
                    >
                      {{ course.course_code }}
                    </h3>
                  </div>

                  <div class="mb-2">
                    <div class="text-gray-500">
                      {{ course.description }}
                    </div>
                  </div>

                  <div class="mt-4">
                    <div class="text-gray-800 font-bold">Prerequisites</div>
                    <div class="text-gray-500" v-if="course.prerequisites">
                      {{ course.prerequisites }}
                    </div>
                  </div>

                  <div class="mt-4">
                    <div class="text-gray-800 font-bold">Credits</div>
                    <div class="text-gray-500">
                      {{ course.credits_units_hours }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="sticky top-5 mt-0" v-else>
              <div class="bg-white shadow-inner sm:rounded-lg">
                <div class="px-4 py-5 sm:p-6 opacity-30">
                  <h2
                    class="text-2xl leading-6 font-medium text-green-800 mb-3"
                  >
                    Course Details
                  </h2>
                  <p>Your selected course will appear here...</p>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "CourseFinder",

  data() {
    return {
      query: "",
      courseName: "",
      showCourseDropdown: false,
      filteredCourses: [],
      institutionId: "",
      catalogId: "",
      courseId: "",
      showDropdown: false,
      filteredColleges: [],
      selectedCollegeName: "",
      isLoadingCourses: false,
      isLoadingCatalogs: false,
      VUE_APP_STATE_NAME: process.env.VUE_APP_STATE_NAME,
      debounceTimeout: null,
      highlightedIndex: -1,
    };
  },

  computed: {
    ...mapGetters({
      allColleges: "college/getAllColleges",
      catalogs: "college/getAllCatalogs",
      allCourses: "college/getAllCourses",
      selectedCourse: "college/getSelectedCourse",
    }),

    colleges() {
      if (this.allColleges.length && this.query) {
        return this.allColleges.filter(({ institutionName }) =>
          institutionName.toLowerCase().includes(this.query.toLowerCase())
        );
      } else {
        return [];
      }
    },

    courses() {
      if (this.allCourses.length && this.courseName) {
        return this.allCourses.filter(({ courseNumber, title }) => {
          const courseSearch = this.courseName.toLowerCase().trim();
          return (
            courseNumber.toLowerCase().includes(courseSearch) ||
            title.toLowerCase().includes(courseSearch)
          );
        });
      } else {
        return [];
      }
    },

    sortedCatalogs() {
      return this.catalogs.slice().sort((a, b) => {
        const startYearA = parseInt(a.edition.split("-")[0]);
        const startYearB = parseInt(b.edition.split("-")[0]);

        return startYearB - startYearA;
      });
    },

    course() {
      return this.selectedCourse;
    },
  },

  watch: {
    institutionId(institutionId) {
      this.setCatalogId();
      this.$store.dispatch("college/resetCatalogs");
      if (institutionId) {
        this.fetchCatalogsByCollege(institutionId);
      }
    },
    catalogId(catalogId) {
      this.courseName = "";
      this.setCourseId();
      this.$store.dispatch("college/resetCourses");
      if (catalogId) {
        this.fetchCoursesByCatalog(catalogId);
      }
    },
  },

  created() {
    this.$store.dispatch("college/resetCollegeState");
    this.$store.dispatch("college/setSelectedCourse", null);
    this.fetchAllColleges();
  },

  methods: {
    async fetchAllColleges() {
      try {
        const endpoint = process.env.VUE_APP_LEVEL
          ? `${process.env.VUE_APP_API_BASE_URL}/institutions/state/level?state=${process.env.VUE_APP_STATE_CODE}&level=${process.env.VUE_APP_LEVEL}`
          : `${process.env.VUE_APP_API_BASE_URL}/institutions/state/?id=${process.env.VUE_APP_STATE_CODE}`;

        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            "x-api-key": process.env.VUE_APP_API_KEY,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        // console.log('Colleges API Response:', data);
        if (data.message && Array.isArray(data.message)) {
          this.$store.commit("college/SET_ALL_COLLEGES", data.message);
        } else {
          this.$store.commit("college/SET_ALL_COLLEGES", []);
        }
      } catch (error) {
        console.error("Error fetching colleges:", error);
        this.$store.commit("college/SET_ALL_COLLEGES", []);
      }
    },

    async fetchCatalogsByCollege(institutionId) {
      this.isLoadingCatalogs = true;
      try {
        const response = await fetch(
          `${process.env.VUE_APP_API_BASE_URL}/catalogs/withcourses?institutionid=${institutionId}`,
          {
            method: "GET",
            headers: {
              "x-api-key": process.env.VUE_APP_API_KEY,
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        // console.log('Catalogs API Response:', data);
        if (data.message && Array.isArray(data.message)) {
          this.$store.commit("college/SET_ALL_CATALOGS", data.message);
        } else {
          this.$store.commit("college/SET_ALL_CATALOGS", []);
        }
      } catch (error) {
        console.error("Error fetching catalogs:", error);
        this.$store.commit("college/SET_ALL_CATALOGS", []);
      } finally {
        this.isLoadingCatalogs = false;
      }
    },

    async fetchCoursesByCatalog(catalogId) {
      this.isLoadingCourses = true;
      try {
        const response = await fetch(
          `${process.env.VUE_APP_API_BASE_URL}/catalogs/${catalogId}/courses?pageSize=1000`,
          {
            method: "GET",
            headers: {
              "x-api-key": process.env.VUE_APP_API_KEY,
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        this.$store.commit("college/SET_ALL_COURSES", data.message || []);
      } catch (error) {
        console.error("Error fetching courses:", error);
        this.$store.commit("college/SET_ALL_COURSES", []);
      } finally {
        this.isLoadingCourses = false;
      }
    },

    handleBlur() {
      setTimeout(() => {
        if (
          !this.isLoadingCourses &&
          !this.courseName &&
          !this.filteredCourses.length
        ) {
          this.showCourseDropdown = false;
        }
      }, 200);
    },

    async searchCourses(searchTerm) {
      if (!this.catalogId || !searchTerm) {
        this.filteredCourses = [];
        return;
      }

      this.isLoadingCourses = true;
      try {
        const response = await fetch(
          `${process.env.VUE_APP_API_BASE_URL}/catalogs/${
            this.catalogId
          }/courses?pageSize=1000&searchTerm=${encodeURIComponent(searchTerm)}`,
          {
            method: "GET",
            headers: {
              "x-api-key": process.env.VUE_APP_API_KEY,
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        this.filteredCourses = data.message || [];
      } catch (error) {
        console.error("Error searching courses:", error);
        this.filteredCourses = [];
      } finally {
        this.isLoadingCourses = false;
        requestAnimationFrame(() => {
          this.$refs.courseInput.focus();
        });
      }
    },

    resetSearch() {
      this.$store.dispatch("college/resetCollegeState");
      this.query = "";
      this.courseName = "";
      this.setInstitutionId();
    },

    setInstitutionId(institutionId = "", institutionName = "") {
      this.institutionId = institutionId;
      this.selectedCollegeName = institutionName;
      this.showDropdown = false;
      this.courseName = "";
      this.courseId = "";
      this.$store.commit("college/SET_ALL_COURSES", []);
    },

    setCatalogId(catalogId = "") {
      this.catalogId = catalogId;
    },

    setCourseId(courseId = "") {
      this.courseId = courseId;
      if (courseId) {
        const selectedCourse = this.filteredCourses.find(
          (course) => course.course_code === courseId
        );
        if (selectedCourse) {
          this.$store.dispatch("college/setSelectedCourse", selectedCourse);
          this.courseName = `${selectedCourse.course_code} - ${selectedCourse.title}`;
        }
      } else {
        this.$store.dispatch("college/setSelectedCourse", null);
        this.courseName = "";
      }
    },

    filterColleges() {
      this.filteredColleges = this.allColleges
        .filter((college) =>
          college.institutionName
            .toLowerCase()
            .includes(this.query.toLowerCase())
        )
        .sort((a, b) => a.institutionName.localeCompare(b.institutionName))
        .slice(0, 10);
    },
    toggleDropdown(status) {
      setTimeout(() => {
        this.showDropdown = status;
      }, 300);
    },

    filterCourses() {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }
      this.showCourseDropdown = true;
      this.debounceTimeout = setTimeout(() => {
        if (this.courseName.trim()) {
          this.searchCourses(this.courseName);
          this.highlightedIndex = -1;
        } else {
          this.filteredCourses = [];
          this.highlightedIndex = -1;
        }
      }, 500);
    },

    highlightNext() {
      if (this.highlightedIndex < this.filteredCourses.length - 1) {
        this.highlightedIndex++;
      }
    },

    highlightPrevious() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
      }
    },

    selectHighlightedCourse() {
      if (
        this.highlightedIndex >= 0 &&
        this.filteredCourses[this.highlightedIndex]
      ) {
        this.setCourseId(
          this.filteredCourses[this.highlightedIndex].course_code
        );
      }
    },
  },
};
</script>

<style>
#app {
  height: calc(100vh);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.fade-in {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

h1 {
  font-family: "Inter var", sans-serif;
}
</style>
